/**
 * ErrorBoundary — React error boundary with fallback UI
 *
 * Catches render errors in child components and shows a user-friendly error message.
 */
import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[ErrorBoundary] Caught error:', error, info);
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;
      return (
        <div className="flex h-full min-h-[200px] items-center justify-center p-8">
          <div className="max-w-md text-center space-y-3">
            <p className="text-3xl">⚠️</p>
            <h3 className="text-lg font-semibold text-destructive">出了点问题</h3>
            <p className="text-sm text-muted-foreground">
              {this.state.error?.message ?? '未知错误'}
            </p>
            <button
              onClick={() => this.setState({ hasError: false, error: null })}
              className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
            >
              重试
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}